import { DSTopNavModule } from '@akso/shuffle-ds';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ErrorHandler,
  OnInit,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { AuthService, IxUser } from '../core/services/auth.service';
import { IxReleaseBanner, ReleaseBannerService } from '../core/services/feature/release-banner.service';
import { LocalStorageSignalService } from '../core/services/local-storage-signal.service';
import { ProjectService } from '../core/services/project.service';
import { UserService } from '../core/services/user.service';
import { LoaderComponent } from '../shared/loading/loader/loader.component';
import { TrackingService } from '../shared/tracking/tracking.service';
import { UserpilotService } from '../shared/userpilot/userpilot.service';
import { tryGetEmailDomain } from '../shared/utils/getEmailDomain';
import { ReleaseBannerComponent } from './release-banner/release-banner.component';
import {
  LS_KEY_SIDE_MENU_TOGGLE,
  SideNavigationComponent,
} from './side-navigation/side-navigation.component';
import { TopNavigationComponent } from './top-navigation/top-navigation.component';

@Component({
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
  imports: [
    TopNavigationComponent,
    SideNavigationComponent,
    RouterOutlet,
    LoaderComponent,
    ReleaseBannerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent implements OnInit {
  readonly toolbar = viewChild.required(DSTopNavModule);
  readonly #authService = inject(AuthService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #projectService = inject(ProjectService);
  readonly #userpilotService = inject(UserpilotService);
  readonly #userService = inject(UserService);
  readonly #trackingService = inject(TrackingService);
  readonly #errorHandler = inject(ErrorHandler);
  readonly #lsService = inject(LocalStorageSignalService);
  readonly #releaseBannerService = inject(ReleaseBannerService);
  readonly bannerToShow = this.#releaseBannerService.bannerToShow;
  readonly isBannerVisible = computed(() => !!this.bannerToShow());
  readonly isSideNavCollapsed = computed(
    () => !this.#lsService.signal<boolean>(LS_KEY_SIDE_MENU_TOGGLE, false)(),
  );

  readonly isLoading = computed(() => this.#projectService.projectStore.isLoading());

  onDismissBanner(banner: IxReleaseBanner): void {
    this.#releaseBannerService.markBannerDismissed(banner);
  }

  ngOnInit(): void {
    this.#setupUserpilot();
    this.#setupTrackingSignIn();
    this.#setupTrackingUserRoles();
    this.#setupTrackingProject();
  }

  #setupUserpilot() {
    this.#userpilotService.loadConfig().pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();

    this.#projectService.currentProject$
      .pipe(
        map(project => project?.id),
        switchMap(projectId => this.#userpilotService.sendProjectId(projectId)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #setupTrackingSignIn() {
    this.#authService.user$
      .pipe(
        filter(oidcUser => !!oidcUser),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((oidcUser: IxUser) => {
        const { id, email } = oidcUser;
        if (!email) {
          return;
        }
        const emailDomain = tryGetEmailDomain(email.toString(), this.#errorHandler);
        this.#trackingService.signIn({ userId: id, emailDomain });
      });
  }

  #setupTrackingUserRoles() {
    this.#userService.currentUserRoles$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(roles => {
      const rolesNames = roles.filter(role => !role.hidden).map(role => role.roleName);

      this.#trackingService.updateUserRoles(rolesNames);
    });
  }

  #setupTrackingProject() {
    this.#projectService.currentProject$
      .pipe(
        distinctUntilChanged((projectA, projectB) => {
          return projectA?.id === projectB?.id;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(project => {
        this.#trackingService.updateSelectedProject(project?.code ?? null, project?.id ?? null);
      });
  }
}
